import { useState, useEffect, useCallback } from 'react';
import umlCanvasEngine from './Core/UmlCanvasEngine';
import umlConnectorEngine from './Core/UmlConnectorEngine';
import umlViewportEngine from './Core/UmlViewportEngine';
import umlSelectionEngine from './Core/UmlSelectionEngine';

// Pure observer op UmlCanvasEngine + UmlViewportEngine (ComponentNotifier-stijl
// pub/sub, geen Context/Provider — zie Feature Folder Structure rk=bb7aae5fec594753aa4df499ae73ed59).
function useUmlSchema() {
  const [, forceRender] = useState(0);

  useEffect(function subscribeToEngines() {
    const unsubscribeSchema = umlCanvasEngine.subscribe(function onSchemaChanged() {
      forceRender(function bumpTick(tick) {
        return tick + 1;
      });
    });
    const unsubscribeConnectors = umlConnectorEngine.subscribe(function onConnectorsChanged() {
      forceRender(function bumpTick(tick) {
        return tick + 1;
      });
    });
    const unsubscribeViewport = umlViewportEngine.subscribe(function onViewportChanged() {
      forceRender(function bumpTick(tick) {
        return tick + 1;
      });
    });
    const unsubscribeSelection = umlSelectionEngine.subscribe(function onSelectionChanged() {
      forceRender(function bumpTick(tick) {
        return tick + 1;
      });
    });
    return function unsubscribeAll() {
      unsubscribeSchema();
      unsubscribeConnectors();
      unsubscribeViewport();
      unsubscribeSelection();
    };
  }, []);

  const moveShape = useCallback(function moveShapeAction(shapeId, x, y, options) {
    umlCanvasEngine.updateShapePosition(shapeId, x, y, options);
  }, []);

  // UmlSeqMessageShape's own draggable label offset — see UmlSeqMessageWidget.
  const moveSeqMessageLabel = useCallback(function moveSeqMessageLabelAction(shapeId, x, y) {
    umlCanvasEngine.updateSeqMessageLabelOffset(shapeId, x, y);
  }, []);

  const resizeShape = useCallback(function resizeShapeAction(shapeId, w, h) {
    umlCanvasEngine.resizeShape(shapeId, w, h);
  }, []);

  const movePoint = useCallback(function movePointAction(pointId, x, y) {
    // pointId kan een UmlConnectorPoint of UmlCaption zijn — beide leven op
    // een connector, niet in shapes[], dus opzoeken via connectors.
    umlConnectorEngine.connectors.forEach(function searchConnector(connector) {
      if (connector.label && connector.label.id === pointId) {
        umlCanvasEngine.updatePointPosition(connector.label, x, y);
        return;
      }
      const point = connector.points.find(function matchPoint(candidate) {
        return candidate.id === pointId;
      });
      if (point) {
        umlCanvasEngine.updatePointPosition(point, x, y);
      }
    });
  }, []);

  // Inplace-edit van de naam/caption van een Package of Class (dubbelklik op
  // de titel, zie UmlPackageWidget.jsx/UmlClassWidget.jsx) — delegeert naar
  // umlCanvasEngine.renameShape, die zelf bepaalt of het shape.name (Package)
  // of shape.caption.text (Class) is.
  const renameShape = useCallback(function renameShapeAction(shapeId, value) {
    umlCanvasEngine.renameShape(shapeId, value);
  }, []);

  // Collapse-toggle op elke box-shape (Class/Package, zie UmlClassWidget.jsx/
  // UmlPackageWidget.jsx) — delegeert naar umlCanvasEngine.setShapeCollapsed,
  // generiek werkend sinds collapsed op UmlShape zit.
  const toggleShapeCollapsed = useCallback(function toggleShapeCollapsedAction(shapeId, collapsed) {
    umlCanvasEngine.setShapeCollapsed(shapeId, collapsed);
  }, []);

  // Icon-picker selectie op een UmlIconShape (zie UmlIconWidget.jsx).
  const setShapeIconKind = useCallback(function setShapeIconKindAction(shapeId, iconKind) {
    umlCanvasEngine.setShapeIconKind(shapeId, iconKind);
  }, []);

  // Toggle voor de "Hide Interfaces"-toolbarknop, zie InterfaceReductionResolver.js.
  const setHideInterfaces = useCallback(function setHideInterfacesAction(value) {
    umlCanvasEngine.setHideInterfaces(value);
  }, []);

  // Sleep-positie voor een "via <Interface>"-derived label, zie InterfaceReductionResolver.js
  // + UmlConnectorPath.jsx.
  const setDerivedLabelOffset = useCallback(function setDerivedLabelOffsetAction(connectorId, x, y) {
    umlCanvasEngine.setDerivedLabelOffset(connectorId, x, y);
  }, []);

  // Hide/show toggle voor een shape- of connector-id (gedeelde lijst,
  // zie resolveUserHiddenItems.js) — werkt voor beide, id's zijn globaal uniek.
  const setItemHidden = useCallback(function setItemHiddenAction(itemId, hidden) {
    umlCanvasEngine.setItemHidden(itemId, hidden);
  }, []);

  // "Fix Lines"-toolbarknop, zie Core/ConnectorFix/ConnectorFixStrategyRegistry.js.
  // visibleConnectors: resolveInterfaceReduction(...).visibleConnectors, doorgegeven door de caller
  // (UmlCanvasToolbar) zodat connectors die via interface-reductie rewired zijn tegen de juiste
  // (zichtbare) shape gecorrigeerd worden, niet tegen de verborgen interface-shape.
  const straightenAllConnectors = useCallback(function straightenAllConnectorsAction(strategyKey, visibleConnectors) {
    umlConnectorEngine.straightenAllConnectors(
      strategyKey,
      function getShapeById(shapeId) {
        return umlCanvasEngine.getShape(shapeId);
      },
      visibleConnectors,
    );
  }, []);

  const getShapeById = useCallback(function getShapeByIdAction(shapeId) {
    return umlCanvasEngine.getShape(shapeId);
  }, []);

  // Click-to-select (see UmlPackageWidget.jsx/UmlClassWidget.jsx) — a shape's
  // caption/title must be selected before a click opens inline edit.
  const selectShape = useCallback(function selectShapeAction(shapeId) {
    umlSelectionEngine.select(shapeId);
    umlCanvasEngine.bringShapeToFront(shapeId);
  }, []);

  // Shift+click (see UmlShapeWidgetBasehandleDragMouseDownHelper.jsx) — toggles
  // shape membership in the multi-selection instead of replacing it.
  const shiftSelectShape = useCallback(function shiftSelectShapeAction(shapeId) {
    umlSelectionEngine.toggleShapeSelection(shapeId);
    umlCanvasEngine.bringShapeToFront(shapeId);
  }, []);

  const clearSelection = useCallback(function clearSelectionAction() {
    umlSelectionEngine.clear();
  }, []);

  return {
    shapes: umlCanvasEngine.shapes,
    connectors: umlConnectorEngine.connectors,
    viewConfig: umlCanvasEngine.viewConfig,
    viewport: {
      scale: umlViewportEngine.scale,
      panX: umlViewportEngine.panX,
      panY: umlViewportEngine.panY,
    },
    moveShape,
    moveSeqMessageLabel,
    resizeShape,
    movePoint,
    renameShape,
    toggleShapeCollapsed,
    setShapeIconKind,
    setHideInterfaces,
    setDerivedLabelOffset,
    setItemHidden,
    straightenAllConnectors,
    getShapeById,
    selectedId: umlSelectionEngine.selectedId,
    selectedShapeIds: umlSelectionEngine.selectedShapeIds,
    selectShape,
    shiftSelectShape,
    clearSelection,
  };
}

export default useUmlSchema;
